import React, { useEffect, useRef, useState } from "react";
import AppShell from "../components/AppShell";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { ScanSmiley, Fingerprint, CheckCircle, XCircle, WarningCircle, Camera, Keyboard, ArrowClockwise } from "@phosphor-icons/react";
import { Html5Qrcode } from "html5-qrcode";

const RESULT_STYLE = {
  FLASH_GREEN: { color: "#00FF66", icon: CheckCircle, label: "APPROVED" },
  FLASH_ORANGE: { color: "#FF9900", icon: WarningCircle, label: "UNPAID" },
  FLASH_RED: { color: "#FF0033", icon: XCircle, label: "DENIED" },
};

export default function Scanner() {
  const [mode, setMode] = useState("camera"); // camera | manual
  const [token, setToken] = useState("");
  const [result, setResult] = useState(null);
  const [flash, setFlash] = useState(null);
  const [busy, setBusy] = useState(false);
  const scannerRef = useRef(null);
  const scannerId = "html5qr-scanner";
  const lastScanRef = useRef({ token: "", ts: 0 });

  const performScan = async (rawToken, isCamera = false) => {
    if (!rawToken) return;
    if (busy) return;
    if (isCamera) {
      const now = Date.now();
      if (lastScanRef.current.token === rawToken && now - lastScanRef.current.ts < 3000) return;
      lastScanRef.current = { token: rawToken, ts: now };
    }
    setBusy(true);
    try {
      const { data } = await api.post("/scan", { token: rawToken });
      setResult(data);
      setFlash(data.alert_color);
      setTimeout(() => setFlash(null), 900);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Scan failed");
    } finally {
      setBusy(false);
    }
  };

  const startCamera = async () => {
    if (scannerRef.current) return;
    try {
      const html5Q = new Html5Qrcode(scannerId, { verbose: false });
      scannerRef.current = html5Q;
      const cams = await Html5Qrcode.getCameras();
      if (!cams || cams.length === 0) {
        toast.error("No camera detected");
        return;
      }
      const camId = cams[cams.length - 1].id;
      await html5Q.start(
        camId,
        { fps: 10, qrbox: { width: 260, height: 260 } },
        (decoded) => performScan(decoded, true),
        () => {}
      );
    } catch (e) {
      toast.error("Camera permission denied or unavailable");
    }
  };

  const stopCamera = async () => {
    try {
      if (scannerRef.current) {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      }
    } catch {}
    scannerRef.current = null;
  };

  useEffect(() => {
    if (mode === "camera") {
      startCamera();
    } else {
      stopCamera();
    }
    return () => {
      stopCamera();
    };
    // eslint-disable-next-line
  }, [mode]);

  const onManualSubmit = (e) => {
    e.preventDefault();
    performScan(token.trim().toUpperCase());
  };

  const currentStyle = result ? RESULT_STYLE[result.alert_color] : null;

  return (
    <AppShell>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">Entry Verification Agent</div>
          <h1 className="heading-font text-4xl sm:text-5xl font-black tracking-tighter mt-2">Scanner</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setMode("camera")} data-testid="scanner-mode-camera"
            className={`inline-flex items-center gap-2 px-4 py-2 border mono-font text-xs uppercase tracking-[0.25em] transition-colors duration-200 ${
              mode === "camera" ? "bg-[#00FF66] text-black border-[#00FF66]" : "text-white/70 border-white/20 hover:border-white/60"
            }`}>
            <Camera size={14} /> Camera
          </button>
          <button onClick={() => setMode("manual")} data-testid="scanner-mode-manual"
            className={`inline-flex items-center gap-2 px-4 py-2 border mono-font text-xs uppercase tracking-[0.25em] transition-colors duration-200 ${
              mode === "manual" ? "bg-[#00FF66] text-black border-[#00FF66]" : "text-white/70 border-white/20 hover:border-white/60"
            }`}>
            <Keyboard size={14} /> Manual
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Scanner viewport */}
        <div className="relative border border-white/10 bg-black overflow-hidden">
          <div className="aspect-square relative">
            {mode === "camera" ? (
              <>
                <div id={scannerId} className="absolute inset-0" />
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <div className="w-64 h-64 border-2 border-[#00FF66]/60 relative border-glow-green">
                    <div className="reticle-line absolute left-0 right-0 h-[2px] bg-[#00FF66] shadow-[0_0_20px_#00FF66]" />
                    {["top-0 left-0","top-0 right-0","bottom-0 left-0","bottom-0 right-0"].map((p, i) => (
                      <span key={i} className={`absolute w-4 h-4 border-[#00FF66] ${p}
                        ${p.includes("top") ? "border-t-2" : "border-b-2"} ${p.includes("left") ? "border-l-2" : "border-r-2"}`} />
                    ))}
                  </div>
                </div>
                <div className="absolute bottom-3 left-3 mono-font text-[10px] tracking-[0.3em] uppercase text-[#00FF66] pulse-glow">
                  ● Scanning
                </div>
              </>
            ) : (
              <form onSubmit={onManualSubmit} className="absolute inset-0 flex flex-col items-center justify-center p-8">
                <ScanSmiley size={48} weight="duotone" className="text-white/40" />
                <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50 mt-4">Manual Token Input</div>
                <input
                  data-testid="scanner-manual-input"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="e.g. A9F3E7B21C0D5F84"
                  className="mt-4 w-full max-w-sm bg-transparent border border-white/20 px-4 py-3 mono-font text-sm text-white text-center tracking-widest uppercase focus:outline-none focus:border-[#00FF66] transition-colors duration-200"
                />
                <button type="submit" data-testid="scanner-manual-submit"
                  className="mt-4 inline-flex items-center gap-2 px-5 py-3 bg-[#00FF66] text-black mono-font text-xs uppercase tracking-[0.25em] hover:bg-white transition-colors duration-200">
                  Verify Token
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Result panel */}
        <div className="border border-white/10 bg-[#0A0A0C] p-6 flex flex-col">
          <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">Last Scan Result</div>
          {result ? (
            <>
              <div className="mt-4 flex items-center gap-3">
                {currentStyle && (
                  <currentStyle.icon size={28} weight="fill" style={{ color: currentStyle.color }} />
                )}
                <div className="heading-font text-3xl font-black tracking-tighter" style={{ color: currentStyle?.color }}
                  data-testid="scan-result-label">
                  {currentStyle?.label}
                </div>
              </div>
              <div className="mt-2 mono-font text-xs uppercase tracking-widest" style={{ color: currentStyle?.color }}
                data-testid="scan-result-reason">
                {result.reason?.replaceAll("_", " ")}
              </div>
              {result.ticket && (
                <div className="mt-6 border border-white/10 p-4 space-y-3">
                  <div>
                    <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">Ticket</div>
                    <div className="mono-font text-sm mt-1">{result.ticket.ticket_id}</div>
                  </div>
                  <div>
                    <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">Bearer</div>
                    <div className="mono-font text-sm mt-1">{result.ticket.buyer_name}</div>
                  </div>
                  <div>
                    <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">Token</div>
                    <div className="mono-font text-xs mt-1 break-all">{result.ticket.token_hash}</div>
                  </div>
                </div>
              )}
              <button onClick={() => { setResult(null); setToken(""); }} data-testid="scan-reset"
                className="mt-auto inline-flex items-center justify-center gap-2 px-4 py-2 border border-white/20 mono-font text-xs uppercase tracking-[0.25em] hover:border-white/60 transition-colors duration-200">
                <ArrowClockwise size={14} /> Ready for Next Scan
              </button>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center py-16">
              <Fingerprint size={40} weight="duotone" className="text-white/30" />
              <div className="mono-font text-xs text-white/40 mt-4">Awaiting scan…</div>
            </div>
          )}
        </div>
      </div>

      {/* Flash overlay */}
      {flash && (
        <div
          className="fixed inset-0 z-[100] pointer-events-none flash-overlay"
          style={{ backgroundColor: flash === "FLASH_GREEN" ? "#00FF66" : flash === "FLASH_ORANGE" ? "#FF9900" : "#FF0033", mixBlendMode: "screen" }}
          data-testid="scan-flash-overlay"
        />
      )}
    </AppShell>
  );
}
